import React, { useState } from 'react';
import { Box, Paper, Typography, useTheme, Grid, Select, MenuItem, FormControl, InputLabel, LinearProgress } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Header } from "../../../../components/global/Header";
import FormListToolbar from "../../../../components/global/Toolbar";
import useSWR from 'swr';
import api from '../../../../services/api';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { EmptyState } from '../../../../components/global';

const EpisodeCostAnalysis = () => {
  const theme = useTheme();
  const [selectedProduction, setSelectedProduction] = useState(null);

  const { data: productions } = useSWR('/productions');
  const { data, error, isLoading } = useSWR(
    selectedProduction 
      ? `/reports/episode-cost-analysis?productionId=${selectedProduction}`
      : '/reports/episode-cost-analysis',
    async (url) => {
      const response = await api.get(url);
      return response;
    }
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <LinearProgress sx={{ width: '100%' }} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Header title="Ανάλυση Κόστους Επεισοδίων" module="Αναφορές" />
        <EmptyState title="Δεν ήταν δυνατή η φόρτωση των δεδομένων" />
      </Box>
    );
  }

  const analysis = data || [];
  const selectedData = selectedProduction 
    ? analysis.find(a => a.productionId === parseInt(selectedProduction))
    : analysis[0];

  if (!selectedData) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Header title="Ανάλυση Κόστους Επεισοδίων" module="Αναφορές" />
        <EmptyState title="Δεν βρέθηκαν δεδομένα" />
      </Box>
    );
  }

  const episodeChartData = selectedData.episodes.map((ep) => ({
    episode: `Επεισόδιο ${ep.episode}`,
    cost: ep.cost,
  }));

  const columns = [
    {
      field: 'episode',
      headerName: 'Επεισόδιο',
      flex: 0.5,
      minWidth: 100,
      align: 'center',
    },
    {
      field: 'cost',
      headerName: 'Κόστος',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'items',
      headerName: 'Στοιχεία',
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Box>
          {params.value.map((item, idx) => (
            <Typography key={idx} variant="caption" display="block">
              {item.item}: {formatCurrency(item.amount)}
            </Typography>
          ))}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Header title="Ανάλυση Κόστους Επεισοδίων" module="Αναφορές" subtitle="Ανάλυση κόστους ανά επεισόδιο" />

      {/* Production Selector */}
      {productions && productions.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth sx={{ maxWidth: 400 }}>
            <InputLabel>Επιλογή Παραγωγής</InputLabel>
            <Select
              value={selectedProduction || ''}
              onChange={(e) => setSelectedProduction(e.target.value)}
              label="Επιλογή Παραγωγής"
            >
              <MenuItem value="">Όλες οι Παραγωγές</MenuItem>
              {productions.map((prod) => (
                <MenuItem key={prod.id} value={prod.id}>
                  {prod.p_descr}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, borderRadius: 3, boxShadow: theme.shadows[3] }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Παραγωγή
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {selectedData.productionName}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, borderRadius: 3, boxShadow: theme.shadows[3] }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Συνολικό Κόστος
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {formatCurrency(selectedData.totalCost)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, borderRadius: 3, boxShadow: theme.shadows[3] }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Μέσο Κόστος/Επεισόδιο
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {formatCurrency(selectedData.averageCost)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, borderRadius: 3, boxShadow: theme.shadows[3] }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Κόστος/Επεισόδιο (Μέσος)
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {formatCurrency(selectedData.costPerEpisode)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3, height: 400, boxShadow: theme.shadows[3] }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Κόστος ανά Επεισόδιο
            </Typography>
            <Box sx={{ height: 320 }}>
              <ResponsiveBar
                data={episodeChartData}
                keys={['cost']}
                indexBy="episode"
                margin={{ top: 20, right: 30, bottom: 100, left: 100 }}
                padding={0.4}
                valueFormat={formatCurrency}
                colors={[theme.palette.primary.main]}
                axisLeft={{
                  format: (value) => formatCurrency(value),
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                }}
                axisBottom={{
                  tickRotation: -45,
                  tickSize: 5,
                  tickPadding: 10,
                }}
                labelSkipWidth={12}
                labelSkipHeight={12}
                labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                theme={{
                  text: {
                    fill: theme.palette.text.primary,
                    fontSize: 12,
                    fontFamily: theme.typography.fontFamily,
                  },
                  grid: {
                    line: {
                      stroke: theme.palette.divider,
                      strokeWidth: 1,
                    },
                  },
                  axis: {
                    ticks: {
                      line: {
                        stroke: theme.palette.divider,
                      },
                      text: {
                        fill: theme.palette.text.secondary,
                        fontSize: 11,
                      },
                    },
                  },
                }}
              />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3, height: 400, boxShadow: theme.shadows[3] }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Στατιστικά Κόστους
            </Typography>
            <Box sx={{ p: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Ελάχιστο Κόστος</Typography>
                <Typography variant="h6">{formatCurrency(selectedData.minCost)}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Μέγιστο Κόστος</Typography>
                <Typography variant="h6">{formatCurrency(selectedData.maxCost)}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Μέσος Όρος</Typography>
                <Typography variant="h6">{formatCurrency(selectedData.averageCost)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Διαφορά</Typography>
                <Typography variant="h6">
                  {formatCurrency(selectedData.maxCost - selectedData.minCost)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Data Grid */}
      <Paper sx={{ p: 2, borderRadius: 3, boxShadow: theme.shadows[3] }}>
        <FormListToolbar disableAdd />
        <Box sx={{ height: 500, width: '100%' }}>
          <DataGrid
            rows={selectedData.episodes.map((ep, index) => ({ ...ep, id: index }))}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            sx={{
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                fontWeight: 600,
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default EpisodeCostAnalysis;

